import { PerformanceContributionColumn } from '@/types/PerformanceContributionColumn';
import { BaseCalcResponseKey, RegressionSummaryCalcResponseKey } from '@/api-v2/web/regression';
import { MetricUnit } from '@/utils/metrics';
import { DEFAULT_DECIMAL_PLACES } from '@/constants/NumberConstants';

const FEE = 'fee';

const metricUnits: {
  [key in BaseCalcResponseKey]: string;
} = {
  // We only want to display a % sign for the residual row Beta which is handled separately
  // the general case is that we dont show a % for the Beta column
  [BaseCalcResponseKey.BETA]: '',
  [BaseCalcResponseKey.STANDARD_ERROR]: '',
  [BaseCalcResponseKey.T_STAT]: '',
  [BaseCalcResponseKey.P_VALUE]: '%',
  [BaseCalcResponseKey.LOWER_BOUND]: '',
  [BaseCalcResponseKey.UPPER_BOUND]: '',
  [BaseCalcResponseKey.VARIANCE_ATTRIBUTION]: '%',
  [BaseCalcResponseKey.VARIANCE_PROPORTION]: '%',
  [BaseCalcResponseKey.RETURN_PA_ATTRIBUTION]: '%',
  [BaseCalcResponseKey.RETURN_PA_PROPORTION]: '%',
  [BaseCalcResponseKey.FACTOR_STANDARD_ERROR]: '',
  [BaseCalcResponseKey.FACTOR_VOL]: '%',
  [BaseCalcResponseKey.FACTOR_CORRELATION]: '',
};

const PerformanceContributionDatumKeys = new Set([
  PerformanceContributionColumn.AVERAGE_WEIGHT,
  PerformanceContributionColumn.EFFECTIVE_RETURN,
  PerformanceContributionColumn.TOTAL_CONTRIBUTION,
  PerformanceContributionColumn.AVERAGE_GROSS_WEIGHT,
  PerformanceContributionColumn.CUMULATIVE_RETURN,
]);

const getSuffix = (key: string, force?: string): string => {
  if (force) return force;

  if (Object.values(BaseCalcResponseKey).includes(key as BaseCalcResponseKey)) {
    return metricUnits[key as BaseCalcResponseKey];
  }

  if (key === RegressionSummaryCalcResponseKey.R_SQUARED) return '%';

  if (PerformanceContributionDatumKeys.has(key as PerformanceContributionColumn)) return '%';

  if (key === FEE) return '%';

  if (key in MetricUnit) return MetricUnit[key as keyof typeof MetricUnit];

  return '';
};

export const displayNum = ({
  value,
  metric,
  numDec = DEFAULT_DECIMAL_PLACES,
  showSuffix = true,
  shouldMultiplyBy100 = false,
  appendPercentSign = false,
}: {
  value: number | null | undefined;
  metric: string;
  numDec?: number;
  showSuffix?: boolean;
  shouldMultiplyBy100?: boolean;
  appendPercentSign?: boolean;
}): string => {
  if (value == null) return '-';

  const valToUse = shouldMultiplyBy100 ? value * 100 : value;

  return `${valToUse.toFixed(numDec)}${showSuffix ? getSuffix(metric, appendPercentSign ? '%' : undefined) : ''}`;
};

export const formatFee = (value: number): string => {
  return displayNum({ value, metric: FEE, shouldMultiplyBy100: true });
};

/**
 * Returns a formatter which formats currency values using browser APIs.
 *
 * The output of Intl.NumberFormat is not guaranteed to be a fixed format.
 *
 * @returns Intl.NumberFormat
 */
export function useNativeNumberFormat(currency: string, baseLocale = 'en-US'): Intl.NumberFormat {
  return new Intl.NumberFormat(baseLocale, {
    style: 'currency',
    currency,
    currencySign: 'accounting',
  });
}

/**
 * There are different reasons why a number should be highlighted -
 * if there is a high pValue, that's bad and so we highlight it
 * for Return Attribution and Variance Proportion, we highlight any negative values
 */
export const shouldHighlightPoorNumber = (
  value: number | null | undefined,
  metric: BaseCalcResponseKey | PerformanceContributionColumn,
): boolean => {
  if (value === null) return false;

  if (value) {
    if (metric === BaseCalcResponseKey.P_VALUE) {
      return value >= 5;
    }

    if (
      metric === BaseCalcResponseKey.RETURN_PA_ATTRIBUTION ||
      metric === BaseCalcResponseKey.VARIANCE_PROPORTION ||
      metric === BaseCalcResponseKey.BETA
    ) {
      return value < 0;
    }
  }

  return false;
};
